import {
  Button,
  Divider,
  FormLabel,
  HStack,
  Heading,
  Input,
  Link,
  Select,
  Text,
  Textarea,
  VStack,
  useToast,
} from '@chakra-ui/react';
import { useQueryClient } from '@tanstack/react-query';
import { useAtom } from 'jotai';
import { useState } from 'react';

import config from '../../../../config';
import {
  LienholderSlugEnum,
  useGeneratePhotoTokenMutation,
} from '../../../../gql/generated/graphql';
import {
  devMarketplaceExternalIdAtom,
  useMarketplaceExternalId,
} from '../../../../services/marketplace/externalId';
import {
  getLeadByExternalIdQueryKey,
  useGetLeadByExternalId,
} from '../../../../services/marketplace/generated/kubb/hooks/useGetLeadByExternalId';
import {
  getLeadByExternalIdConditionReportSchemaQueryKey,
  useGetLeadByExternalIdConditionReportSchema,
} from '../../../../services/marketplace/generated/kubb/hooks/useGetLeadByExternalIdConditionReportSchema';
import { usePatchLeadByExternalIdData } from '../../../../services/marketplace/generated/kubb/hooks/usePatchLeadByExternalIdData';
import { usePatchLeadByExternalIdOffersOfferId } from '../../../../services/marketplace/generated/kubb/hooks/usePatchLeadByExternalIdOffersOfferId';
import {
  LeadDetailStatusEnumKey,
  leadDetailStatusEnum,
} from '../../../../services/marketplace/generated/kubb/models/LeadDetail';
import { UserDeal } from '../../utils';
import {
  devGuidanceScenarioAtom,
  devLeadStatusAtom,
  devLienholderSlugAtom,
} from '../devToolbarAtoms';
import {
  SchemaQuestion,
  SchemaStep,
  allActiveQuestions,
  encodeAnswers,
} from './conditionReport/encodeDecodeAnswers';
import { PHOTOS_LIVE_VIEW_THRESHOLD } from './constants';
import { DEV_GUIDANCE_SAMPLES, DevGuidanceScenario } from './devGuidanceSamples';
import useMarketplacePhotoDevTools from './hooks/useMarketplacePhotoDevTools';

const seedAnswerForQuestion = (q: SchemaQuestion): unknown => {
  switch (q.type) {
    case 'boolean':
      return false;
    case 'enum':
      return q.options?.[0]?.value;
    case 'multi_select': {
      const exclusive = q.options?.find((o) => o.exclusive);
      if (exclusive) return { issues: [], noIssues: true };
      const first = q.options?.[0]?.value;
      return first ? { issues: [first], noIssues: false } : { issues: [], noIssues: false };
    }
    case 'number':
      return 1;
    case 'text':
      return 'Seed';
    default:
      return undefined;
  }
};

const buildSeedAnswers = (steps: SchemaStep[]): Record<string, unknown> => {
  const out: Record<string, unknown> = {};
  allActiveQuestions(steps).forEach((q) => {
    const seed = seedAnswerForQuestion(q);
    if (seed != null) out[q.questionId] = seed;
  });
  return out;
};

type MarketplaceDevToolsProps = {
  userDeal: UserDeal;
};

const MarketplaceDevTools = ({ userDeal }: MarketplaceDevToolsProps) => {
  const dealId = userDeal?.id || '';
  const externalId = useMarketplaceExternalId(dealId);
  const [generatePhotoToken, { data: photoTokenData, loading: generatingPhotoToken }] =
    useGeneratePhotoTokenMutation();
  const [devMarketplaceExternalId, setDevMarketplaceExternalId] = useAtom(
    devMarketplaceExternalIdAtom,
  );
  const [devLeadStatus, setDevLeadStatus] = useAtom(devLeadStatusAtom);
  const [devGuidanceScenario, setDevGuidanceScenario] = useAtom(devGuidanceScenarioAtom);
  const [devLienholderSlug, setDevLienholderSlug] = useAtom(devLienholderSlugAtom);
  const toast = useToast();
  const queryClient = useQueryClient();
  const {
    photoActionState,
    handleDeleteAllPhotos,
    handleSeedPrefilledPhotoSet,
    handleSeedThresholdPhotoSet,
  } = useMarketplacePhotoDevTools({ externalId });

  const [showSchema, setShowSchema] = useState(false);
  const conditionReportSchemaQuery = useGetLeadByExternalIdConditionReportSchema({ externalId });
  const leadQuery = useGetLeadByExternalId({ externalId });
  const activeDriverAsk = (leadQuery.data?.offers ?? []).find(
    (o) => o.type === 'driver_ask' && o.status === 'pending',
  );

  const { mutateAsync: withdrawOffer, isPending: withdrawPending } =
    usePatchLeadByExternalIdOffersOfferId({
      mutation: {
        onSuccess: () =>
          queryClient.invalidateQueries({
            queryKey: getLeadByExternalIdQueryKey({ externalId }),
          }),
      },
    });

  const { mutateAsync: patchLeadMutation, isPending: patchLeadMutationPending } =
    usePatchLeadByExternalIdData({
      mutation: {
        onSuccess: () => {
          queryClient.invalidateQueries({
            queryKey: getLeadByExternalIdQueryKey({ externalId }),
          });
          queryClient.invalidateQueries({
            queryKey: getLeadByExternalIdConditionReportSchemaQueryKey({ externalId }),
          });
        },
      },
    });

  const handleDeleteConditionReport = async () => {
    await patchLeadMutation({
      externalId,
      data: { conditionReport: null },
    });
  };

  const handleWithdrawOffer = async () => {
    if (!activeDriverAsk || !externalId) return;
    try {
      await withdrawOffer({
        externalId,
        offerId: activeDriverAsk.id,
        data: { action: 'withdraw' },
      });
      toast({ title: 'Offer withdrawn', status: 'success', duration: 2000, position: 'top' });
    } catch (err) {
      toast({
        title: 'Could not withdraw offer',
        description: (err as { message?: string })?.message,
        status: 'error',
        duration: 4000,
        position: 'top',
      });
    }
  };

  const handleSeedConditionReport = async () => {
    let steps = conditionReportSchemaQuery.data?.steps;
    if (!steps) {
      const result = await conditionReportSchemaQuery.refetch();
      steps = result.data?.steps;
      if (!steps) {
        toast({ title: 'Could not load schema', status: 'error', duration: 3000, position: 'top' });
        return;
      }
    }
    const questions = allActiveQuestions(steps);
    await patchLeadMutation({
      externalId,
      data: { conditionReport: encodeAnswers(buildSeedAnswers(steps), questions) },
    });
  };

  return (
    <VStack
      align="stretch"
      borderWidth={2}
      borderColor="grayBlueish"
      borderRadius="lg"
      p="10px"
      w="320px"
    >
      <Heading size="sm" textAlign="center">
        Marketplace Dev Tools
      </Heading>

      <Text>
        Marketplace deal:{' '}
        {leadQuery.data?.id ? (
          <Link href={`${config.marketplaceApiUrl}/leads/${leadQuery.data.id}`} target="_blank">
            {leadQuery.data.id}
          </Link>
        ) : (
          '--'
        )}
      </Text>

      <HStack>
        <FormLabel width="100px" fontSize="sm">
          External ID
        </FormLabel>
        <Input
          placeholder="Override external ID"
          value={devMarketplaceExternalId}
          onChange={(e) => setDevMarketplaceExternalId(e.target.value)}
          bg="white"
          size="sm"
        />
      </HStack>

      <HStack>
        <FormLabel width="100px" fontSize="sm">
          Lead status
        </FormLabel>
        <Select
          value={devLeadStatus}
          onChange={(e) => setDevLeadStatus(e.target.value as LeadDetailStatusEnumKey | '')}
          bg="white"
          size="sm"
        >
          <option value="">No override ({leadQuery.data?.status ?? '--'})</option>
          {Object.values(leadDetailStatusEnum).map((status) => (
            <option key={status} value={status}>
              {status}
            </option>
          ))}
        </Select>
      </HStack>

      <HStack>
        <FormLabel width="100px" fontSize="sm">
          Guidance scenario
        </FormLabel>
        <Select
          value={devGuidanceScenario}
          onChange={(e) => setDevGuidanceScenario(e.target.value as DevGuidanceScenario | '')}
          bg="white"
          size="sm"
        >
          <option value="">No override</option>
          {Object.keys(DEV_GUIDANCE_SAMPLES).map((scenario) => (
            <option key={scenario} value={scenario}>
              {scenario}
            </option>
          ))}
        </Select>
      </HStack>

      <HStack>
        <FormLabel width="100px" fontSize="sm">
          Lienholder
        </FormLabel>
        <Select
          value={devLienholderSlug}
          onChange={(e) => setDevLienholderSlug(e.target.value as LienholderSlugEnum | '')}
          bg="white"
          size="sm"
        >
          <option value="">No override ({leadQuery.data?.lienholderSlug ?? '--'})</option>
          {Object.values(LienholderSlugEnum).map((slug) => (
            <option key={slug} value={slug}>
              {slug}
            </option>
          ))}
        </Select>
      </HStack>

      <Heading size="xs" color="grayLight">
        Condition report
      </Heading>
      <Button
        size="sm"
        onClick={handleSeedConditionReport}
        isLoading={patchLeadMutationPending}
        loadingText="Saving..."
        isDisabled={!dealId}
      >
        Seed prefilled
      </Button>
      <Button
        size="sm"
        variant="outline"
        onClick={handleDeleteConditionReport}
        isLoading={patchLeadMutationPending}
        loadingText="Deleting..."
        isDisabled={!dealId}
      >
        Delete condition report
      </Button>
      <Button
        size="sm"
        variant="ghost"
        onClick={() => setShowSchema((p) => !p)}
        isLoading={conditionReportSchemaQuery.isFetching}
        loadingText="Fetching..."
      >
        Fetch schema
      </Button>
      {showSchema && (
        <Textarea
          value={JSON.stringify(conditionReportSchemaQuery.data, null, 2)}
          readOnly
          rows={10}
          fontSize="xs"
          fontFamily="mono"
          bg="white"
          onFocus={(e) => e.currentTarget.select()}
        />
      )}

      <Divider />

      <Heading size="xs" color="grayLight">
        Photos
      </Heading>
      <Button
        size="sm"
        onClick={handleSeedPrefilledPhotoSet}
        isLoading={photoActionState === 'seeding'}
        loadingText="Seeding..."
        isDisabled={!externalId || photoActionState != null}
      >
        Seed prefilled photo set
      </Button>
      <Button
        size="sm"
        onClick={handleSeedThresholdPhotoSet}
        isLoading={photoActionState === 'seedingPartial'}
        loadingText="Seeding..."
        isDisabled={!externalId || photoActionState != null}
      >
        Seed {PHOTOS_LIVE_VIEW_THRESHOLD} photos
      </Button>
      <Button
        size="sm"
        variant="outline"
        onClick={handleDeleteAllPhotos}
        isLoading={photoActionState === 'clearing'}
        loadingText="Deleting..."
        isDisabled={
          !externalId || photoActionState === 'seeding' || photoActionState === 'seedingPartial'
        }
      >
        Delete all photos
      </Button>
      <Button
        size="sm"
        variant="ghost"
        onClick={() =>
          generatePhotoToken({
            variables: { dealId: userDeal?.id ?? '' },
          })
        }
        isLoading={generatingPhotoToken}
        loadingText="Generating Photo Token..."
        isDisabled={!userDeal?.id}
      >
        Generate photo token
      </Button>

      <Divider />

      <Heading size="xs" color="grayLight">
        Asking price
      </Heading>
      <Button
        size="sm"
        variant="outline"
        onClick={handleWithdrawOffer}
        isLoading={withdrawPending}
        loadingText="Withdrawing..."
        isDisabled={!activeDriverAsk || !externalId}
      >
        Withdraw asking price
      </Button>

      {photoTokenData?.generatePhotoToken?.token && (
        <Link
          href={`${config.baseUrl}/photos/capture?token=${photoTokenData.generatePhotoToken.token}`}
          target="_blank"
          fontSize="xs"
          color="blue.500"
          wordBreak="break-all"
        >
          {config.baseUrl}/photos/capture?token={photoTokenData.generatePhotoToken.token}
        </Link>
      )}
    </VStack>
  );
};

export default MarketplaceDevTools;
